/**
 * The admin console: the shape of the deployment as tiles, and every account
 * in it as a table.
 *
 * **It shows who is here and what they are allowed. It does not show what
 * anybody spent.** There is no amount on this screen, no balance, no expense
 * and no receipt — not even a thumbnail — and that is not a rule this
 * component follows carefully. It is a rule about the data: `server/admin.ts`
 * reads no table that holds money, `AdminUser` in `src/api.ts` has no field
 * that could hold one, and `tests/admin.test.ts` walks every response of every
 * `/api/admin` route to keep both true. There is nothing here to be careful
 * with.
 *
 * **The middle of the table is the limit resolver's answer, not this screen's
 * reading of a plan.** "6 people" and "12 people — raised" are the same digit
 * and different facts, so the provenance is in the words beside the number
 * rather than in a colour: the second one is what a support conversation is
 * about, and an admin scanning a hundred rows for it needs it to be a word.
 *
 * **A filter is a URL the server answers, not a `.filter()` over a page.**
 * Searching, narrowing by status, by plan or by which limit somebody has
 * overridden all go back to the server, because the screen holds fifty of a
 * thousand accounts and filtering the fifty would quietly answer a different
 * question from the one that was asked.
 */

import { useState, type FormEvent } from 'react'
import type { AccountState, AdminUser } from '../api'
import { staggerStyle } from '../motion-react'
import {
  OVERRIDE_FILTERS,
  STATUS_FILTERS,
  actionsFor,
  day,
  describeAudit,
  describeGroupLimit,
  describeKind,
  describeLastSeen,
  describeLimitKey,
  describeOverride,
  describeState,
  describeUploads,
  emptyMessage,
  formatBytes,
  parseLimitValue,
  tilesFor,
  type StatusFilter,
} from '../lib/admin'
import {
  useAdminStats,
  useAdminUser,
  useAdminUsers,
  useClearAdminLimit,
  useSetAdminLimit,
  useSetAdminPlan,
  useSetAdminState,
} from '../queries'

export function Admin({ isAdmin }: { isAdmin: boolean }) {
  const [q, setQ] = useState('')
  /** Which account the panel below the table is open on, if any. */
  const [openId, setOpenId] = useState<string | null>(null)
  /** What is in the box, separate from what has been asked for. */
  const [typed, setTyped] = useState('')
  const [status, setStatus] = useState<StatusFilter>('any')
  const [plan, setPlan] = useState('any')
  const [overrides, setOverrides] = useState('any')

  const query = { q, status, plan, overrides }
  const stats = useAdminStats(isAdmin)
  const users = useAdminUsers(query, isAdmin)

  /*
   * The console is role-gated in three places and only one of them is the
   * enforcement. `server/admin.ts` refuses a request from somebody who is not
   * an admin; this refuses to *ask*, so a non-admin who types the URL gets a
   * sentence instead of a spinner and then a 403; and `App.tsx` does not offer
   * the link. The first is the one that matters — a client's idea of who it is
   * is as old as its last request.
   */
  if (!isAdmin) {
    return (
      <section className="card">
        <h2>Admin</h2>
        <p className="muted">
          This screen is for site admins. Your account is not one, and asking again from a
          different address will get the same answer.
        </p>
      </section>
    )
  }

  const rows = users.data?.pages.flatMap((page) => page.users) ?? []
  const matching = users.data?.pages[0]?.matching ?? null
  const plans = stats.data?.plans ?? []

  return (
    <>
      <section className="card">
        <h2>Admin</h2>
        <p className="muted note">
          Accounts, plans and what they are using. There is nothing about anybody’s money on
          this screen, and no route behind it that could put it here.
        </p>

        {stats.isPending && <p className="muted">Counting…</p>}
        {stats.isError && <p className="bad">{stats.error.message}</p>}

        {stats.data && (
          <ul className="tiles">
            {tilesFor(stats.data).map((tile, index) => (
              <li key={tile.key} className="tile" style={staggerStyle(index)}>
                <p className="tile__label">{tile.label}</p>
                <p className="tile__value">{tile.value}</p>
                <p className="tile__hint">{tile.hint}</p>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="card">
        <h2>Accounts</h2>

        <form
          className="admin__filters"
          onSubmit={(event) => {
            event.preventDefault()
            setQ(typed)
          }}
        >
          <span className="admin__search">
            <label htmlFor="admin-q">Search</label>
            <input
              id="admin-q"
              type="search"
              value={typed}
              placeholder="A name or an address"
              onChange={(event) => setTyped(event.target.value)}
            />
            <button type="submit" className="button--quiet">
              Search
            </button>
          </span>

          <span className="admin__filter">
            <label htmlFor="admin-status">Status</label>
            <select
              id="admin-status"
              value={status}
              onChange={(event) => setStatus(event.target.value as StatusFilter)}
            >
              {STATUS_FILTERS.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          </span>

          <span className="admin__filter">
            <label htmlFor="admin-plan">Plan</label>
            {/* The plans come from the stats read rather than from a list in
                the client: a plan is a row somebody can add, and a hard-coded
                dropdown is the second place plans would then be configured. */}
            <select id="admin-plan" value={plan} onChange={(event) => setPlan(event.target.value)}>
              <option value="any">Any plan</option>
              {plans.map((option) => (
                <option key={option.planId} value={option.planId}>
                  {option.name}
                </option>
              ))}
            </select>
          </span>

          <span className="admin__filter">
            <label htmlFor="admin-overrides">Overrides</label>
            <select
              id="admin-overrides"
              value={overrides}
              onChange={(event) => setOverrides(event.target.value)}
            >
              {OVERRIDE_FILTERS.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          </span>
        </form>

        {users.isPending && <p className="muted">Fetching accounts…</p>}
        {users.isError && <p className="bad">{users.error.message}</p>}

        {matching !== null && (
          <p className="muted note">
            {matching.toLocaleString('en-US')} {matching === 1 ? 'account' : 'accounts'} match,
            showing {rows.length.toLocaleString('en-US')}.
          </p>
        )}

        {users.data && rows.length === 0 && <p className="muted">{emptyMessage(query)}</p>}

        {rows.length > 0 && (
          <div className="admin__scroll">
            <table className="admin__table">
              <thead>
                <tr>
                  <th scope="col">Account</th>
                  <th scope="col">Plan</th>
                  <th scope="col">Status</th>
                  <th scope="col">Attachments</th>
                  <th scope="col">Group limit</th>
                  <th scope="col" className="admin__number">
                    Groups
                  </th>
                  <th scope="col" className="admin__number">
                    Files
                  </th>
                  <th scope="col" className="admin__number">
                    Stored
                  </th>
                  <th scope="col">Last seen</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((user) => (
                  <Row
                    key={user.id}
                    user={user}
                    open={user.id === openId}
                    onOpen={() => setOpenId(user.id === openId ? null : user.id)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {users.hasNextPage && (
          <p className="actions">
            <button
              type="button"
              className="button--quiet"
              disabled={users.isFetchingNextPage}
              onClick={() => void users.fetchNextPage()}
            >
              {users.isFetchingNextPage ? 'Fetching…' : 'More accounts'}
            </button>
          </p>
        )}
      </section>

      {openId !== null && <Account userId={openId} onClose={() => setOpenId(null)} />}
    </>
  )
}

function Row({
  user,
  open,
  onOpen,
}: {
  user: AdminUser
  open: boolean
  onOpen: () => void
}) {
  const badges = describeKind(user)

  return (
    <tr className={user.state === 'active' ? undefined : 'admin__row--off'}>
      <th scope="row" className="admin__who">
        <button type="button" className="admin__open" aria-expanded={open} onClick={onOpen}>
          {open ? 'Close' : 'Open'}
        </button>
        <span className="admin__name">{user.displayName}</span>
        <span className="admin__email">{user.email}</span>
        {badges.length > 0 && (
          <span className="admin__badges">
            {badges.map((badge) => (
              <span key={badge} className="pill">
                {badge}
              </span>
            ))}
          </span>
        )}
        <span className="admin__since">Joined {day(user.createdAt)}</span>
      </th>
      <td>{user.planId ?? '—'}</td>
      <td>
        {describeState(user.state)}
        {user.statusReason && <span className="admin__note">{user.statusReason}</span>}
      </td>
      {/* The switch, and never a bare "yes": an admin turning it off is the
          only reason it is ever anything but on, so the word has to carry it. */}
      <td className={user.uploads.allowed ? undefined : 'admin__off'}>
        {describeUploads(user.uploads)}
      </td>
      <td className={user.groupLimit.source === 'override' ? 'admin__raised' : undefined}>
        {describeGroupLimit(user.groupLimit)}
        {user.overrides.length > 1 && (
          <span className="admin__note">
            {user.overrides.length} overrides:{' '}
            {user.overrides.map((override) => override.limitKey.replace(/_/g, ' ')).join(', ')}
          </span>
        )}
      </td>
      <td className="admin__number">{user.groups}</td>
      <td className="admin__number">{user.files}</td>
      <td className="admin__number">{formatBytes(user.storageBytesUsed)}</td>
      <td>{describeLastSeen(user.lastSeenAt)}</td>
    </tr>
  )
}

/**
 * One account, and everything an admin may do to it.
 *
 * It is a panel under the table rather than a screen of its own, because the
 * question it answers ("what is going on with this person") is asked while
 * scanning the table and answered by going straight back to it. Every write on
 * it carries a reason, because the server refuses without one: the reason is
 * what makes the `admin_audit` row worth having six months later, and the
 * moment to write it is the moment the decision is being made rather than a
 * form somebody fills in afterwards.
 *
 * The two irreversible-ish buttons ask again. A ban is about somebody else's
 * ability to come back and a delete cannot be undone at all, and neither is
 * something to discover having done.
 */
function Account({ userId, onClose }: { userId: string; onClose: () => void }) {
  const account = useAdminUser(userId)
  const user = account.data?.user

  if (account.isPending) {
    return (
      <section className="card">
        <p className="muted">Fetching the account…</p>
      </section>
    )
  }
  if (account.isError || !user) {
    return (
      <section className="card">
        <p className="bad">{account.error?.message ?? 'That account could not be read.'}</p>
      </section>
    )
  }

  return (
    <section className="card admin__panel">
      <div className="admin__panel-head">
        <h2>{user.displayName}</h2>
        <button type="button" className="button--quiet" onClick={onClose}>
          Close
        </button>
      </div>
      <p className="muted note">
        {user.email} · {describeState(user.state)}
        {user.statusReason ? ` — ${user.statusReason}` : ''}
        {user.statusChangedAt ? ` (${day(user.statusChangedAt)})` : ''}
      </p>

      {user.state === 'deleted' ? (
        <p className="muted">
          This account has been deleted. The name, the address and the files are gone; every
          expense it was part of is exactly where it was, so nobody else’s balance moved. There
          is nothing left to change.
        </p>
      ) : (
        <>
          <PlanEditor user={user} />
          <UploadsSwitch user={user} />
          <LimitEditor user={user} />
          <StateButtons user={user} />
        </>
      )}

      <h3>Overrides</h3>
      {user.limitOverrides.length === 0 ? (
        <p className="muted">Nothing has been overridden on this account.</p>
      ) : (
        <ul className="admin__list">
          {user.limitOverrides.map((override) => (
            <li key={override.limitKey} className={override.live ? undefined : 'admin__off'}>
              <strong>{describeLimitKey(override.limitKey)}</strong>
              <span className="admin__note">{describeOverride(override)}</span>
              {override.reason && <span className="admin__note">“{override.reason}”</span>}
            </li>
          ))}
        </ul>
      )}

      {user.blocks.length > 0 && (
        <>
          <h3>Blocked</h3>
          <ul className="admin__list">
            {user.blocks.map((block) => (
              <li key={block.type}>
                <strong>{block.type.replace(/_/g, " ")}</strong>
                <span className="admin__note">
                  since {day(block.since)}
                  {block.reason ? ` — ${block.reason}` : ""}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}

      <h3>What has been done</h3>
      {user.audit.length === 0 ? (
        <p className="muted">Nothing yet.</p>
      ) : (
        <ul className="admin__list">
          {user.audit.map((entry) => (
            <li key={entry.id}>
              <span>{describeAudit(entry)}</span>
              <span className="admin__note">
                {day(entry.at)}
                {entry.reason ? ` — “${entry.reason}”` : ""}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

/** The reason box every one of these forms carries, and the refusal beside it. */
function Reason({
  id,
  value,
  onChange,
}: {
  id: string
  value: string
  onChange: (value: string) => void
}) {
  return (
    <span className="admin__field">
      <label htmlFor={id}>Reason</label>
      <input
        id={id}
        type="text"
        required
        value={value}
        placeholder="Why, for whoever reads this later"
        onChange={(event) => onChange(event.target.value)}
      />
    </span>
  )
}

function Failure({ error }: { error: Error | null }) {
  return error ? <p className="bad">{error.message}</p> : null
}

function PlanEditor({ user }: { user: { id: string; planId: string | null } }) {
  const stats = useAdminStats(true)
  const setPlan = useSetAdminPlan(user.id)
  const [planId, setPlanId] = useState(user.planId ?? "")
  const [reason, setReason] = useState("")

  const submit = (event: FormEvent) => {
    event.preventDefault()
    setPlan.mutate({ planId: planId === "" ? null : planId, reason })
  }

  return (
    <form className="admin__form" onSubmit={submit}>
      <h3>Plan</h3>
      <span className="admin__field">
        <label htmlFor="admin-set-plan">On</label>
        <select
          id="admin-set-plan"
          value={planId}
          onChange={(event) => setPlanId(event.target.value)}
        >
          {/* Blank is the default plan, which is what almost every row holds —
              not "no plan", which is not a thing an account can be. */}
          <option value="">The default plan</option>
          {(stats.data?.plans ?? []).map((plan) => (
            <option key={plan.planId} value={plan.planId}>
              {plan.name}
            </option>
          ))}
        </select>
      </span>
      <Reason id="admin-plan-reason" value={reason} onChange={setReason} />
      <button type="submit" disabled={setPlan.isPending || reason.trim() === ""}>
        {setPlan.isPending ? "Saving…" : "Set plan"}
      </button>
      <Failure error={setPlan.error} />
    </form>
  )
}

/**
 * The attachment switch.
 *
 * It has a button of its own because it is the one an admin arrives looking
 * for, and it is still the override editor underneath: `can_upload_attachments`
 * is a limit, resolved by the same function as every other limit, and this
 * sends `1` or `0` through the same route. A second write path for it would be
 * a second place an override is written.
 */
function UploadsSwitch({
  user,
}: {
  user: { id: string; uploads: { allowed: boolean; source: string } }
}) {
  const setLimit = useSetAdminLimit(user.id)
  const clearLimit = useClearAdminLimit(user.id)
  const [reason, setReason] = useState("")
  const busy = setLimit.isPending || clearLimit.isPending
  const blank = reason.trim() === ""

  return (
    <div className="admin__form">
      <h3>Attachments</h3>
      <p className="muted note">
        {user.uploads.allowed
          ? "They can attach files. Turning this off stops the next upload, not the ones already stored."
          : "Uploads are refused. Everything already attached stays where it is."}
      </p>
      <Reason id="admin-uploads-reason" value={reason} onChange={setReason} />
      <span className="actions">
        <button
          type="button"
          disabled={busy || blank}
          onClick={() =>
            setLimit.mutate({
              limitKey: "can_upload_attachments",
              value: user.uploads.allowed ? 0 : 1,
              reason,
            })
          }
        >
          {user.uploads.allowed ? "Turn uploads off" : "Turn uploads on"}
        </button>
        {user.uploads.source === "override" && (
          <button
            type="button"
            className="button--quiet"
            disabled={busy || blank}
            onClick={() =>
              clearLimit.mutate({ limitKey: "can_upload_attachments", reason })
            }
          >
            Back to the plan’s
          </button>
        )}
      </span>
      <Failure error={setLimit.error ?? clearLimit.error} />
    </div>
  )
}

/**
 * One override: which limit, what to, until when, and why.
 *
 * The keys come from the server (`limitKeys`), so a limit added to the
 * database is offered here without a deploy — which is the whole reason limits
 * are rows. The value is parsed before it is sent, because "a whole number, or
 * blank for no limit" is the same sentence whether this says it now or the
 * server says it a round trip later.
 */
function LimitEditor({
  user,
}: {
  user: { id: string; limitKeys: string[]; limitOverrides: { limitKey: string }[] }
}) {
  const setLimit = useSetAdminLimit(user.id)
  const clearLimit = useClearAdminLimit(user.id)
  const [limitKey, setLimitKey] = useState(user.limitKeys[0] ?? "max_group_size")
  const [typed, setTyped] = useState("")
  const [expiresAt, setExpiresAt] = useState("")
  const [reason, setReason] = useState("")

  const parsed = parseLimitValue(typed)
  const busy = setLimit.isPending || clearLimit.isPending
  const blank = reason.trim() === ""
  const overridden = user.limitOverrides.some((row) => row.limitKey === limitKey)

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (!parsed.ok) return
    setLimit.mutate({
      limitKey,
      value: parsed.value,
      reason,
      expiresAt: expiresAt === "" ? null : expiresAt,
    })
  }

  return (
    <form className="admin__form" onSubmit={submit}>
      <h3>One limit</h3>
      <span className="admin__field">
        <label htmlFor="admin-limit-key">Limit</label>
        <select
          id="admin-limit-key"
          value={limitKey}
          onChange={(event) => setLimitKey(event.target.value)}
        >
          {user.limitKeys.map((key) => (
            <option key={key} value={key}>
              {describeLimitKey(key)}
            </option>
          ))}
        </select>
      </span>
      <span className="admin__field">
        <label htmlFor="admin-limit-value">To</label>
        <input
          id="admin-limit-value"
          type="text"
          inputMode="numeric"
          value={typed}
          placeholder="Blank for no limit"
          onChange={(event) => setTyped(event.target.value)}
        />
      </span>
      <span className="admin__field">
        <label htmlFor="admin-limit-expires">Until</label>
        <input
          id="admin-limit-expires"
          type="date"
          value={expiresAt}
          onChange={(event) => setExpiresAt(event.target.value)}
        />
      </span>
      <Reason id="admin-limit-reason" value={reason} onChange={setReason} />
      <span className="actions">
        <button type="submit" disabled={busy || blank || !parsed.ok}>
          {setLimit.isPending ? "Saving…" : "Set the override"}
        </button>
        {overridden && (
          <button
            type="button"
            className="button--quiet"
            disabled={busy || blank}
            onClick={() => clearLimit.mutate({ limitKey, reason })}
          >
            Clear it
          </button>
        )}
      </span>
      {!parsed.ok && <p className="bad">{parsed.error}</p>}
      <Failure error={setLimit.error ?? clearLimit.error} />
    </form>
  )
}

function StateButtons({ user }: { user: { id: string; state: AccountState } }) {
  const setState = useSetAdminState(user.id)
  const [reason, setReason] = useState("")
  /** Which button has been pressed once and is waiting to be pressed again. */
  const [asking, setAsking] = useState<AccountState | null>(null)

  const blank = reason.trim() === ""

  return (
    <div className="admin__form">
      <h3>The account</h3>
      <Reason id="admin-state-reason" value={reason} onChange={setReason} />
      <ul className="admin__actions">
        {actionsFor(user.state).map((action) => (
          <li key={action.state}>
            <button
              type="button"
              className={action.state === "deleted" ? "button--danger" : undefined}
              disabled={setState.isPending || blank}
              onClick={() => {
                if (action.confirm && asking !== action.state) {
                  setAsking(action.state)
                  return
                }
                setAsking(null)
                setState.mutate({ state: action.state, reason })
              }}
            >
              {asking === action.state ? `${action.label} — press again` : action.label}
            </button>
            <span className="admin__note">{action.detail}</span>
          </li>
        ))}
      </ul>
      <Failure error={setState.error} />
    </div>
  )
}
